import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";

import { AppDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { PATHS } from "@/lib/router";
import NxmapSlider from "./NxmapSlider";
import NxplanSlider from "./NxplanSlider";
import EsgSlider from "./EsgSlider";
import NxopsSlider from "./NxopsSlider";
import PcafSlider from "./PcafSlider";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth";
import { checkUserFromRole } from "@/helpers";

const TourModal = ({ solution, onClose }) => {
  const navigate = useNavigate();

  const { user, userRole } = useAuthStore();
  const permissions = user?.permissions || [];
  const isUserLevel = checkUserFromRole(userRole);

  const open = Boolean(solution);
  if (!open) {
    return null;
  }

  const handleExplore = () => {
    if (!solution?.id) return;
    navigate(`${PATHS.ONBOARD}/${solution.id}`);
    onClose?.();
  };

  const canExplore = solution?.status === "active";
  const title = solution?.title || "";
  const description = solution?.description || "";

  const renderSlider = () => {
    if (solution?.id === "nxmap") {
      return <NxmapSlider showExplore={false} onExplore={handleExplore} />;
    }
    if (solution?.id === "nxplan") {
      return <NxplanSlider showExplore={false} onExplore={handleExplore} />;
    }
    if (solution?.id === "esg") {
      return <EsgSlider showExplore={false} onExplore={handleExplore} />;
    }
    if (solution?.id === "nxops" && canExplore) {
      return <NxopsSlider showExplore={false} onExplore={handleExplore} />;
    }
    if (solution?.id === "pcaf" && canExplore) {
      return <PcafSlider showExplore={false} onExplore={handleExplore} />;
    }

    return (
      <>
        {solution.image ? (
          <img
            src={solution.image}
            alt={`${solution.title} visual`}
            className="mb-3 h-44 w-full rounded-radius object-cover"
            loading="lazy"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = "/logos/logo.svg";
            }}
          />
        ) : null}
        {solution.description ? (
          <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
            {solution.description}
          </p>
        ) : null}
        {Array.isArray(solution.bullets) && solution.bullets.length > 0 ? (
          <div className="mt-4 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            {solution.bullets.map((bullet) => (
              <div key={bullet} className="flex items-center gap-4">
                <span className="flex h-2.5 w-2.5 flex-shrink-0 items-center justify-center rounded-full border border-nxstart-green/30 bg-nxstart-green/80" />
                <p className="leading-relaxed">{bullet}</p>
              </div>
            ))}
          </div>
        ) : null}
      </>
    );
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose?.();
      }}
      size="2xl"
      hideCloseButton
      contentClassName="!p-0 [&>div:first-child]:hidden"
      bodyClassName="!space-y-0 !p-0"
    >
      <div className="flex flex-col gap-4 bg-background px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-6">
        <div>
          <p className="text-lg font-semibold text-foreground">{title}</p>
          {description ? (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          {canExplore ? (
            <Can
              resource="onboard_setup"
              action="create"
              enabled={!isUserLevel}
              userPermissions={permissions}
            >
              <Button
                type="button"
                size="sm"
                onClick={handleExplore}
                className="w-full sm:w-auto"
              >
                Explore
              </Button>
            </Can>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onClose?.()}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {renderSlider()}
    </AppDialog>
  );
};

export default TourModal;
